import { useState } from "react";
import { Mail, MessageSquare, Send, CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PublicPage, PageHero, Seo } from "@/components/public/PublicChrome";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      toast.success(data.message || "Message sent!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <PublicPage>
      <Seo title="Contact" path="/contact" description="Contact the Fairview Systems team for questions, support, or information about our business platform and free tools — we're here to help and reply quickly." />
      <PageHero eyebrow="Contact" title="Let's talk"
        subtitle="Questions about the platform, billing, or getting started? Send us a message and we'll get back to you shortly." />
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-3 gap-10">
          <div className="space-y-6">
            <div className="flex items-start gap-3"><div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#D4AF37]" /></div>
              <div><div className="text-white font-medium">Email</div><div className="text-sm text-[#A1A1AA]">support@fairviewsystems.com</div></div></div>
            <div className="flex items-start gap-3"><div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0"><MessageSquare className="w-5 h-5 text-[#D4AF37]" /></div>
              <div><div className="text-white font-medium">Sales & billing</div><div className="text-sm text-[#A1A1AA]">billing@fairviewsystems.com</div></div></div>
            <p className="text-sm text-[#71717A] leading-relaxed">Prefer to explore first? Try the <a href="/demo" className="text-[#D4AF37] hover:underline">Interactive Demo</a> — no account needed.</p>
          </div>

          <div className="lg:col-span-2">
            {sent ? (
              <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-10 text-center" data-testid="contact-success">
                <div className="w-14 h-14 rounded-2xl fv-gold-gradient mx-auto flex items-center justify-center mb-4"><CheckCircle2 className="w-7 h-7 text-[#0A0A0B]" /></div>
                <h3 className="font-heading text-2xl mb-2">Message sent</h3>
                <p className="text-[#A1A1AA]">Thanks for reaching out — we'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-2xl border border-white/8 bg-[#121214] p-7 space-y-4" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-[#A1A1AA] text-sm">Name</Label>
                    <Input required value={form.name} onChange={set("name")} data-testid="contact-name" placeholder="Your name" className="bg-[#0d0d0f] border-white/10 text-white mt-1.5" /></div>
                  <div><Label className="text-[#A1A1AA] text-sm">Email</Label>
                    <Input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email" placeholder="you@company.com" className="bg-[#0d0d0f] border-white/10 text-white mt-1.5" /></div>
                </div>
                <div><Label className="text-[#A1A1AA] text-sm">Subject</Label>
                  <Input value={form.subject} onChange={set("subject")} data-testid="contact-subject" placeholder="How can we help?" className="bg-[#0d0d0f] border-white/10 text-white mt-1.5" /></div>
                <div><Label className="text-[#A1A1AA] text-sm">Message</Label>
                  <Textarea required value={form.message} onChange={set("message")} data-testid="contact-message" placeholder="Tell us a bit more…" rows={5} className="bg-[#0d0d0f] border-white/10 text-white mt-1.5" /></div>
                <Button type="submit" disabled={loading} data-testid="contact-submit"
                  className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full px-7 h-11 w-full sm:w-auto">
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send message</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
